import { useEffect } from "react";

export interface ShortcutCommand {
  id: string;
  keys: string[];
  action: () => void;
  global?: boolean;
}

interface ParsedKey {
  mod: boolean;
  shift: boolean;
  alt: boolean;
  key: string;
}

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

function parseKey(combo: string): ParsedKey {
  const parts = combo.toLowerCase().split("+");
  return {
    mod: parts.includes("mod"),
    shift: parts.includes("shift"),
    alt: parts.includes("alt"),
    key: parts.filter((p) => !["mod", "shift", "alt"].includes(p))[0] ?? "",
  };
}

function matchesEvent(parsed: ParsedKey, e: KeyboardEvent): boolean {
  const modPressed = isMac ? e.metaKey : e.ctrlKey;
  if (parsed.mod !== modPressed) return false;
  if (parsed.alt !== e.altKey) return false;
  // For character keys produced by Shift (like ?), skip the shift check
  // Only enforce shift when explicitly declared in the combo
  const isShiftedChar = !parsed.mod && !parsed.alt && !parsed.shift && e.key.length === 1;
  if (!isShiftedChar && parsed.shift !== e.shiftKey) return false;
  return e.key.toLowerCase() === parsed.key;
}

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts(commands: ShortcutCommand[]): void {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Ignore bare modifier presses
      if (["Meta", "Control", "Shift", "Alt"].includes(e.key)) return;

      const inInput = isInputFocused();

      for (const cmd of commands) {
        if (inInput && !cmd.global) continue;

        for (const keyCombo of cmd.keys) {
          if (matchesEvent(parseKey(keyCombo), e)) {
            e.preventDefault();
            cmd.action();
            return;
          }
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [commands]);
}
